import { collection, getDocs, query, where } from 'firebase/firestore';
import type { Firestore } from 'firebase/firestore';
import {
  FIRESTORE_BROKER_PURCHASE_ORDERS_COLLECTION,
  FIRESTORE_PURCHASE_ORDERS_COLLECTION,
  FIRESTORE_PO_MARKETPLACE_FLAG,
  FIRESTORE_MARKETPLACE_OPEN_LISTING_IS_MARKETPLACE,
} from '../constants/firestoreCollectionSecurity';
import { isPurchaseOrderActiveForActionRequired } from '../services/notificationFirestoreQueries';
import Logger from '../services/logger';

const FIRESTORE_IN_QUERY_MAX = 30;

/** Child multi-load leg — has a parent PO distinct from its own poNumber. */
export function resolveMultiLoadChildParentPoNumber(
  poData: Record<string, unknown> | null | undefined
): string | null {
  if (!poData) return null;
  const parentPONumber = String(poData.parentPONumber || '').trim();
  const poNumber = String(poData.poNumber || '').trim();
  if (!parentPONumber || parentPONumber === poNumber) return null;
  return parentPONumber;
}

export function isMultiLoadChildPoData(
  poData: Record<string, unknown> | null | undefined
): boolean {
  return resolveMultiLoadChildParentPoNumber(poData) != null;
}

/** Parent multi-load PO shell is still bookable (not deleted/completed). */
export function isValidMultiLoadParentPoData(
  poData: Record<string, unknown> | undefined | null
): boolean {
  if (!poData) return false;
  const poNumber = String(poData.poNumber || '').trim();
  if (!poNumber) return false;
  const parentPONumber = String(poData.parentPONumber || '').trim();
  if (parentPONumber && parentPONumber !== poNumber) return false;
  if (poData.isMultiLoad !== true) return false;
  return isPurchaseOrderActiveForActionRequired(poData);
}

/**
 * Client parent-gate resolution under R2.
 * - valid: parent doc readable and still an active multi-load shell
 * - knownInvalid: parent doc readable but terminal / not a parent shell
 * - unknown: parent not returned (denied query or not marketplace-readable) — defer to Admin batch-exists
 */
export type MultiLoadParentGateResolution = {
  valid: Set<string>;
  knownInvalid: Set<string>;
};

export type MultiLoadChildParentGateDecision = 'allow' | 'deny' | 'unknown';

/** Child marketplace POs require a live parent shell — orphans are not open listings. */
export function isMultiLoadChildOpenListingAllowed(
  childPoData: Record<string, unknown>,
  validParentPoNumbers: ReadonlySet<string>
): boolean {
  const parent = resolveMultiLoadChildParentPoNumber(childPoData);
  if (!parent) return true;
  return validParentPoNumbers.has(parent);
}

/**
 * Distinguish unknown (R2 cannot prove parent) from deny (parent positively invalid).
 * Used so batch-exists can recover when client parent list queries are incomplete.
 */
export function resolveMultiLoadChildParentGateDecision(
  childPoData: Record<string, unknown>,
  resolution: MultiLoadParentGateResolution
): MultiLoadChildParentGateDecision {
  const parent = resolveMultiLoadChildParentPoNumber(childPoData);
  if (!parent) return 'allow';
  if (resolution.valid.has(parent)) return 'allow';
  if (resolution.knownInvalid.has(parent)) return 'deny';
  return 'unknown';
}

/**
 * Batch-resolve parent PO numbers for marketplace child browse.
 * Prefer marketplace-scoped list queries (carrier-readable under R2). Party-unscoped
 * poNumber-only queries soft-fail for carriers and must not be treated as "parent invalid".
 */
export async function batchResolveMultiLoadParentPoNumbers(
  db: Firestore,
  parentPONumbers: string[]
): Promise<MultiLoadParentGateResolution> {
  const unique = [
    ...new Set(parentPONumbers.map((p) => String(p || '').trim()).filter(Boolean)),
  ];
  const valid = new Set<string>();
  const knownInvalid = new Set<string>();
  if (unique.length === 0) return { valid, knownInvalid };

  const seen = new Set<string>();

  const apply = (data: Record<string, unknown>, chunk: string[]) => {
    const pn = String(data.poNumber || '').trim();
    if (!pn || !chunk.includes(pn)) return;
    seen.add(pn);
    if (isValidMultiLoadParentPoData(data)) {
      valid.add(pn);
      knownInvalid.delete(pn);
    } else if (!valid.has(pn)) {
      knownInvalid.add(pn);
    }
  };

  for (let i = 0; i < unique.length; i += FIRESTORE_IN_QUERY_MAX) {
    const chunk = unique.slice(i, i + FIRESTORE_IN_QUERY_MAX);

    for (const collectionName of [
      FIRESTORE_PURCHASE_ORDERS_COLLECTION,
      FIRESTORE_BROKER_PURCHASE_ORDERS_COLLECTION,
    ] as const) {
      // R2-safe: marketplace listing read (isMarketplace + unassigned carrierId on rule)
      try {
        const marketplaceSnap = await getDocs(
          query(
            collection(db, collectionName),
            where('poNumber', 'in', chunk),
            where(
              FIRESTORE_PO_MARKETPLACE_FLAG,
              '==',
              FIRESTORE_MARKETPLACE_OPEN_LISTING_IS_MARKETPLACE
            )
          )
        );
        marketplaceSnap.docs.forEach((d) => apply(d.data() as Record<string, unknown>, chunk));
      } catch (err) {
        Logger.warn(
          `[batchResolveMultiLoadParentPoNumbers] ${collectionName} marketplace parent query unavailable`,
          err instanceof Error ? err : new Error(String(err))
        );
      }

      // Best-effort party-scoped unscoped list (works for owners; denied for carriers — soft-fail)
      const unresolved = chunk.filter((pn) => !seen.has(pn));
      if (unresolved.length === 0) continue;
      try {
        const snap = await getDocs(
          query(collection(db, collectionName), where('poNumber', 'in', unresolved))
        );
        snap.docs.forEach((d) => apply(d.data() as Record<string, unknown>, unresolved));
      } catch (err) {
        Logger.warn(
          `[batchResolveMultiLoadParentPoNumbers] ${collectionName} parent query unavailable`,
          err instanceof Error ? err : new Error(String(err))
        );
      }
    }
  }

  return { valid, knownInvalid };
}

/**
 * @deprecated Prefer batchResolveMultiLoadParentPoNumbers — returns only proven-valid parents.
 * Unknown parents are omitted (not treated as invalid).
 */
export async function batchValidMultiLoadParentPoNumbers(
  db: Firestore,
  parentPONumbers: string[]
): Promise<Set<string>> {
  const { valid } = await batchResolveMultiLoadParentPoNumbers(db, parentPONumbers);
  return valid;
}
